import { z } from 'zod';

/**
 * Mailboxes on the Mail Server, managed only through the portal (ARCHITECTURE_CORRECTION_PLAN §F).
 * Approved operations only: list (with usage and quota), create, change quota. Quotas are checked
 * server-side before any Mail Server call (DECISIONS.md A-K15): at least 1 GB for everyone, at
 * most 5 GB for ICT, above 5 GB for the Admin only. Delete, disable, password reset, aliases,
 * forwarding, rename and editing comments or retrieval features are not approved (OD-1) and have
 * no endpoint.
 */
const quotaBytesSchema = z.number().int().positive().max(Number.MAX_SAFE_INTEGER);

/** One mailbox. Each optional fact is null when the Mail Server does not report it. */
export const mailboxSchema = z.object({
  address: z.string(),
  /** Decimal bytes, as the Mail Server's (1 GB = 1,000,000,000); null: no limit (unlimited). */
  quotaBytes: z.number().int().nullable(),
  /** Null when the Mail Server does not report usage. */
  usedBytes: z.number().int().nullable(),
  comment: z.string().nullable(),
  imapEnabled: z.boolean().nullable(),
  pop3Enabled: z.boolean().nullable(),
});
export type Mailbox = z.infer<typeof mailboxSchema>;

export const mailboxListSchema = z.object({ items: z.array(mailboxSchema) });

export const mailboxCreateSchema = z.strictObject({
  address: z.string().trim().toLowerCase().email().max(254),
  quotaBytes: quotaBytesSchema,
  /**
   * Initial password, passed to the Mail Server once. Never stored, logged, audited or returned.
   * The Mail Server applies its own password rules.
   */
  password: z.string().min(1).max(512),
});
export type MailboxCreate = z.infer<typeof mailboxCreateSchema>;

export const mailboxQuotaUpdateSchema = z.strictObject({ quotaBytes: quotaBytesSchema });
export type MailboxQuotaUpdate = z.infer<typeof mailboxQuotaUpdateSchema>;

export const mailboxParamsSchema = z.object({
  address: z.string().trim().toLowerCase().email().max(254),
});
